import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import type {
  ClinicalInteractionsResponse,
  ClinicalInteractionOverride,
  DrugDrugInteraction,
  DuplicateTherapy,
  AllergyAlert,
} from '../../types/clinicalInteractions.types';
import type { SafetyAcknowledgment } from '../../types/clinicalVerification.types';

// ─── Severity helpers (shared across clinical verification & counseling) ──
const getSeverityInfo = (level: number) => {
  switch (level) {
    case 1: return { label: 'Contraindicated', color: '#d32f2f', bg: 'rgba(211,47,47,0.06)' };
    case 2: return { label: 'Severe', color: '#d32f2f', bg: 'rgba(211,47,47,0.04)' };
    case 3: return { label: 'Moderate', color: '#ed6c02', bg: 'rgba(237,108,2,0.04)' };
    case 4: return { label: 'Minor', color: '#9e9e9e', bg: 'transparent' };
    default: return { label: 'Unknown', color: '#9e9e9e', bg: 'transparent' };
  }
};

const formatCompactDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(2);
    return `${day}/${month}/${year}`;
  } catch {
    return '—';
  }
};

interface ClinicalInteractionsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Full screening response (clinical verification mode) */
  data?: ClinicalInteractionsResponse | null;
  /** DDI-only list (counseling mode) — used when `data` is not provided */
  ddiList?: DrugDrugInteraction[];
  /** DIN → fill date map for showing fill dates per drug group */
  dinFillDates?: Record<string, string>;
  /** Current drug DIN — used to determine "other" drug in DDI-only mode */
  currentDin?: string;
  /** Current drug GCN — fallback matching when DIN doesn't match */
  currentGcn?: number;
  /** Duplicate therapies to show (DDI-only mode) */
  duplicateTherapies?: DuplicateTherapy[];
  /** Allergy alerts to show (DDI-only mode) */
  allergyAlerts?: AllergyAlert[];
  loading: boolean;
  error?: string | null;
  patientName?: string;
  /** Drug name shown in the title (counseling mode) */
  drugName?: string;
  pharmacistName?: string;
  onOverrideComplete?: (override: ClinicalInteractionOverride) => void;
  existingOverride?: ClinicalInteractionOverride | null;
  onSafetyAcknowledge?: (ack: SafetyAcknowledgment) => void;
}

const ClinicalInteractionsDialog: React.FC<ClinicalInteractionsDialogProps> = ({
  open,
  onClose,
  data,
  ddiList,
  dinFillDates,
  currentDin,
  currentGcn,
  duplicateTherapies: propDuplicateTherapies,
  allergyAlerts: propAllergyAlerts,
  loading,
  error,
  patientName,
  drugName,
  pharmacistName,
  onOverrideComplete,
  existingOverride,
  onSafetyAcknowledge,
}) => {
  const [expandedDrugGroups, setExpandedDrugGroups] = useState<Set<string>>(new Set());
  const [reviewedDDIs, setReviewedDDIs] = useState<Set<string>>(new Set());

  // DDI-only mode: when ddiList is provided instead of full data
  const isDdiOnlyMode = !data && !!ddiList;

  // Reset reviewed state when data changes (switching drugs triggers new data)
  useEffect(() => {
    setReviewedDDIs(new Set());
    setExpandedDrugGroups(new Set());
  }, [data, ddiList]);

  const summary = data?.summary;
  const requiresOverride = summary?.requiresOverride && !existingOverride;

  // Group DDIs by the "other" drug (not the current order)
  const groupedDDIs = useMemo(() => {
    // DDI-only mode (counseling): use ddiList directly
    if (isDdiOnlyMode && ddiList) {
      const matchesCurrent = (drug: { din: string; gcn?: number }) =>
        drug.din === currentDin || (currentGcn != null && drug.gcn === currentGcn);

      const drugMap = new Map<string, {
        drugKey: string;
        drugName: string;
        fillDate: string | null;
        worstSeverity: number;
        interactions: Array<{
          description: string;
          clinicalEffect?: string;
          severityLevel: number;
        }>;
      }>();

      ddiList.forEach((ddi, idx) => {
        const isD1Current = matchesCurrent(ddi.drug1);
        const other = isD1Current ? ddi.drug2 : ddi.drug1;
        const otherKey = other?.din || other?.name || `unknown_${idx}`;

        if (!drugMap.has(otherKey)) {
          drugMap.set(otherKey, {
            drugKey: otherKey,
            drugName: other?.name || otherKey,
            fillDate: dinFillDates?.[other?.din || ''] || null,
            worstSeverity: 99,
            interactions: [],
          });
        }
        const group = drugMap.get(otherKey)!;
        group.worstSeverity = Math.min(group.worstSeverity, ddi.severityLevel);
        group.interactions.push({
          description: ddi.description || '',
          clinicalEffect: ddi.clinicalEffect,
          severityLevel: ddi.severityLevel,
        });
      });

      return [...drugMap.values()].sort((a, b) => {
        if (a.worstSeverity !== b.worstSeverity) return a.worstSeverity - b.worstSeverity;
        if (!a.fillDate && !b.fillDate) return 0;
        if (!a.fillDate) return 1;
        if (!b.fillDate) return -1;
        return b.fillDate.localeCompare(a.fillDate);
      });
    }

    // Full data mode (clinical verification)
    if (!data?.drugDrugInteractions?.length) return [];

    const currentDins = new Set(
      (data.screenedDrugs || []).filter(d => d.isCurrentOrder).map(d => d.din),
    );

    const drugMap = new Map<string, {
      drugKey: string;
      drugName: string;
      fillDate: string | null;
      worstSeverity: number;
      interactions: Array<{
        description: string;
        clinicalEffect?: string;
        severityLevel: number;
      }>;
    }>();

    // Only show DDIs involving the current order drug
    const relevantDdis = data.drugDrugInteractions.filter(ddi => ddi.involvesCurrentOrder);

    for (const ddi of relevantDdis) {
      const isD1Current = currentDins.has(ddi.drug1?.din || '');
      const isD2Current = currentDins.has(ddi.drug2?.din || '');
      const other = isD1Current ? ddi.drug2 : isD2Current ? ddi.drug1 : ddi.drug2;
      const otherKey = other?.din || other?.name || '';
      if (!otherKey) continue;

      if (!drugMap.has(otherKey)) {
        drugMap.set(otherKey, {
          drugKey: otherKey,
          drugName: other?.name || otherKey,
          fillDate: null,
          worstSeverity: ddi.severityLevel,
          interactions: [],
        });
      }
      const entry = drugMap.get(otherKey)!;
      entry.worstSeverity = Math.min(entry.worstSeverity, ddi.severityLevel);
      entry.interactions.push({
        description: ddi.description || '',
        clinicalEffect: ddi.clinicalEffect,
        severityLevel: ddi.severityLevel,
      });
    }

    return [...drugMap.values()].sort((a, b) => a.worstSeverity - b.worstSeverity);
  }, [data, ddiList, isDdiOnlyMode, currentDin, currentGcn, dinFillDates]);

  // Only show duplicate therapies involving the current order drug
  const relevantDuplicates = useMemo(() => {
    if (isDdiOnlyMode) {
      return propDuplicateTherapies || [];
    }
    if (!data?.duplicateTherapies?.length) return [];
    return data.duplicateTherapies.filter(dup =>
      dup.drugs.some(d => d.isCurrentOrder),
    );
  }, [data, isDdiOnlyMode, propDuplicateTherapies]);

  // Allergy alerts: use prop in DDI-only mode, data in full mode
  const activeAllergyAlerts = isDdiOnlyMode
    ? (propAllergyAlerts || [])
    : (data?.allergyAlerts || []);

  const totalDrugGroups = groupedDDIs.length
    + activeAllergyAlerts.length
    + relevantDuplicates.length;
  const reviewedCount = reviewedDDIs.size;

  const toggleReviewed = (key: string) => {
    setReviewedDDIs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const markAllReviewed = () => {
    const all = new Set<string>();
    groupedDDIs.forEach(g => all.add(g.drugKey));
    activeAllergyAlerts.forEach((_, i) => all.add(`allergy-${i}`));
    relevantDuplicates.forEach((_, i) => all.add(`dup-${i}`));
    setReviewedDDIs(all);
  };

  const handleAcknowledgeAll = () => {
    markAllReviewed();

    // DDI-only mode (counseling): just close
    if (isDdiOnlyMode) {
      onClose();
      return;
    }

    // No relevant interactions for this drug — just acknowledge and proceed
    if (totalDrugGroups === 0) {
      if (onSafetyAcknowledge) {
        onSafetyAcknowledge({
          reason: 'no_interaction',
          acknowledgedAt: new Date().toISOString(),
          acknowledgedBy: pharmacistName || '',
        });
      } else {
        onClose();
      }
      return;
    }

    if (requiresOverride && onOverrideComplete) {
      onOverrideComplete({
        overriddenAt: new Date().toISOString(),
        overriddenBy: pharmacistName || '',
        overriddenByInitials: '',
        overrideReason: 'reviewed',
        overrideNotes: '',
        alertsSummary: {
          directAllergyAlerts: data?.allergyAlerts?.filter(a => a.severity === 'red').length || 0,
          crossSensitivityAlerts: data?.allergyAlerts?.filter(a => a.severity === 'orange').length || 0,
          severeInteractions: data?.drugDrugInteractions?.filter(
            i => (i.severityLevel === 1 || i.severityLevel === 2) && i.involvesCurrentOrder,
          ).length || 0,
          totalAlertsOverridden: summary?.totalAlerts || 0,
        },
        alerts: [
          ...(data?.allergyAlerts || []).map(a => ({
            type: (a.matchType === 'direct' ? 'allergy_direct' : 'allergy_cross') as 'allergy_direct' | 'allergy_cross',
            drugName: a.drugName,
            description: `${a.allergyText} → ${a.drugName}`,
          })),
          ...(data?.drugDrugInteractions || [])
            .filter(i => (i.severityLevel === 1 || i.severityLevel === 2) && i.involvesCurrentOrder)
            .map(i => ({
              type: 'ddi_severe' as const,
              drugName: `${i.drug1?.name} + ${i.drug2?.name}`,
              description: i.description || '',
            })),
        ],
      });
    } else if (onSafetyAcknowledge) {
      onSafetyAcknowledge({
        reason: 'reviewed',
        acknowledgedAt: new Date().toISOString(),
        acknowledgedBy: pharmacistName || '',
      });
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#fff8f0', py: 1, borderBottom: '2px solid #ed6c02', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
          <Typography variant="subtitle1" component="span" fontWeight="bold">
            Interactions{drugName ? ` — ${drugName}` : patientName ? ` — ${patientName}` : ''}
          </Typography>
          {totalDrugGroups > 0 && (
            <Chip
              label={`${totalDrugGroups} item${totalDrugGroups !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {reviewedCount < totalDrugGroups && totalDrugGroups > 0 && (
            <Button size="small" onClick={markAllReviewed} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              Mark All Reviewed
            </Button>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography>Screening patient profile...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {existingOverride && (
          <Alert severity="info" sx={{ m: 2 }} icon={<CheckCircleIcon />}>
            Override completed by {existingOverride.overriddenBy} — {existingOverride.alertsSummary.totalAlertsOverridden} alerts acknowledged.
          </Alert>
        )}

        {(data || isDdiOnlyMode) && !loading && (
          <>
            {/* No interactions for this drug */}
            {totalDrugGroups === 0 && (
              <Alert severity="success" sx={{ m: 2 }} icon={<CheckCircleIcon />}>
                No interactions found for this drug.
              </Alert>
            )}

            {/* Allergy alerts — shown as red items */}
            {activeAllergyAlerts.map((alert, i) => {
              const key = `allergy-${i}`;
              const isReviewed = reviewedDDIs.has(key);
              return (
                <Box key={key}>
                  <Box
                    onClick={() => toggleReviewed(key)}
                    sx={{
                      px: 1.5, py: 0.75,
                      bgcolor: isReviewed ? 'rgba(46,125,50,0.06)' : 'rgba(211,47,47,0.06)',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      cursor: 'pointer',
                      '&:hover': { filter: 'brightness(0.97)' },
                    }}
                  >
                    <Checkbox
                      checked={isReviewed}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleReviewed(key)}
                      size="small"
                      color="success"
                      sx={{ p: 0.25 }}
                    />
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                      {alert.drugName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', flex: 1 }}>
                      — Allergy: {alert.allergyText}
                      {alert.crossSensitivityDesc && ` (cross-sensitivity: ${alert.crossSensitivityDesc})`}
                    </Typography>
                    <Chip
                      label={alert.severity === 'red' ? 'Allergy' : 'Cross-Sensitivity'}
                      size="small"
                      sx={{
                        ml: 'auto',
                        bgcolor: alert.severity === 'red' ? 'rgba(211,47,47,0.08)' : 'rgba(237,108,2,0.06)',
                        color: alert.severity === 'red' ? '#d32f2f' : '#ed6c02',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 20,
                        border: `1px solid ${alert.severity === 'red' ? '#d32f2f' : '#ed6c02'}`,
                      }}
                    />
                  </Box>
                </Box>
              );
            })}

            {/* Drug-Drug Interactions — flat list matching counseling pattern */}
            {groupedDDIs.map((group) => {
              const groupSev = getSeverityInfo(group.worstSeverity);
              const isExpanded = expandedDrugGroups.has(group.drugKey);
              const isReviewed = reviewedDDIs.has(group.drugKey);
              return (
                <Box key={group.drugKey}>
                  <Box
                    onClick={() => setExpandedDrugGroups(prev => {
                      const next = new Set(prev);
                      if (next.has(group.drugKey)) next.delete(group.drugKey);
                      else next.add(group.drugKey);
                      return next;
                    })}
                    sx={{
                      px: 1.5, py: 0.75,
                      bgcolor: isReviewed ? 'rgba(46,125,50,0.06)' : groupSev.bg,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      cursor: 'pointer',
                      '&:hover': { filter: 'brightness(0.97)' },
                    }}
                  >
                    <Checkbox
                      checked={isReviewed}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleReviewed(group.drugKey);
                      }}
                      size="small"
                      color="success"
                      sx={{ p: 0.25 }}
                    />
                    {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                      {group.drugName}
                    </Typography>
                    {group.fillDate && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        filled {formatCompactDate(group.fillDate)}
                      </Typography>
                    )}
                    <Chip
                      label={groupSev.label}
                      size="small"
                      sx={{ ml: 'auto', bgcolor: groupSev.bg, color: groupSev.color, fontWeight: 600, fontSize: '0.7rem', height: 20, border: `1px solid ${groupSev.color}` }}
                    />
                  </Box>

                  <Collapse in={isExpanded} timeout="auto">
                    <Box sx={{ px: 2, py: 0.5, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
                      {group.interactions.map((inter, j) => {
                        const sev = getSeverityInfo(inter.severityLevel);
                        return (
                          <Box key={j} sx={{ py: 0.2, pl: 1, ml: 0.5, borderLeft: `3px solid ${sev.color}`, '&:not(:last-child)': { mb: 0.15 } }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
                              {inter.description}{inter.clinicalEffect ? ` — ${inter.clinicalEffect}` : ''}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            {/* Duplicate Therapy — only those involving current order */}
            {relevantDuplicates.map((dup, i) => {
              const key = `dup-${i}`;
              const isReviewed = reviewedDDIs.has(key);
              return (
                <Box key={key}>
                  <Box
                    onClick={() => toggleReviewed(key)}
                    sx={{
                      px: 1.5, py: 0.75,
                      bgcolor: isReviewed ? 'rgba(46,125,50,0.06)' : 'rgba(237,108,2,0.04)',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      cursor: 'pointer',
                      '&:hover': { filter: 'brightness(0.97)' },
                    }}
                  >
                    <Checkbox
                      checked={isReviewed}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleReviewed(key)}
                      size="small"
                      color="success"
                      sx={{ p: 0.25 }}
                    />
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                      {dup.therapyClass}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', flex: 1 }}>
                      — {[...new Set(dup.drugs.map(d => d.name))].join(', ')}
                    </Typography>
                    <Chip
                      label="Duplicate Therapy"
                      size="small"
                      sx={{
                        ml: 'auto',
                        bgcolor: 'rgba(237,108,2,0.06)',
                        color: '#ed6c02',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 20,
                        border: '1px solid #ed6c02',
                      }}
                    />
                  </Box>
                </Box>
              );
            })}

            {/* Unmatched allergies info (not in DDI-only mode) */}
            {!isDdiOnlyMode && (data?.unmatchedAllergies?.length || 0) > 0 && (
              <Alert severity="info" sx={{ m: 1, py: 0 }}>
                {data!.unmatchedAllergies.length} patient allergy/allergies could not be matched to FDB for screening: {data!.unmatchedAllergies.join(', ')}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {(data || isDdiOnlyMode) && !loading && !existingOverride && (
          <Button
            variant="contained"
            onClick={handleAcknowledgeAll}
            startIcon={<CheckCircleIcon />}
            color="success"
          >
            Acknowledge All & Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClinicalInteractionsDialog;
